'use client';

import React from 'react';

/**
 * Shared route-transition skeleton.
 *
 * App modules fetch their data client-side from Supabase, so without a
 * `loading.tsx` a navigation shows a blank frame until the query resolves.
 * This mirrors the AppLayout shape (sidebar + content) so the transition
 * doesn't jump, and announces itself to assistive tech.
 */
export default function AppLoadingSkeleton({ label }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="min-h-screen bg-background flex"
    >
      <span className="sr-only">{label ?? 'Loading…'}</span>

      {/* Sidebar rail */}
      <div className="hidden md:block w-56 shrink-0 border-r border-border p-4 space-y-3">
        <div className="h-8 w-32 rounded bg-muted animate-pulse" />
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="h-7 rounded bg-muted/60 animate-pulse" />
        ))}
      </div>

      {/* Content column */}
      <div className="flex-1 p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-7 w-48 rounded bg-muted animate-pulse" />
          <div className="h-8 w-28 rounded bg-muted animate-pulse" />
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-3">
              <div className="h-3 w-20 rounded bg-muted animate-pulse" />
              <div className="h-7 w-24 rounded bg-muted animate-pulse" />
            </div>
          ))}
        </div>

        {/* Table / chart block */}
        <div className="rounded-xl border border-border p-4 space-y-3">
          <div className="h-5 w-40 rounded bg-muted animate-pulse" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="h-4 flex-1 rounded bg-muted/60 animate-pulse" />
              <div className="h-4 w-24 rounded bg-muted/60 animate-pulse" />
              <div className="h-4 w-20 rounded bg-muted/60 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
